import * as Sentry from '@sentry/react';
import Keycloak, { type KeycloakConfig } from 'keycloak-js';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getInitialLanguage, t } from '@/i18n';
import type { ROLE } from '../common/role';
import {
	clearKeycloakSession,
	ensureFreshToken,
	isSessionEndedError,
	registerKeycloakSession,
	TOKEN_REFRESH_MARGIN_SECONDS,
} from '../lib/utils/keycloak-session';
import { setAccessToken } from '../lib/utils/tokens';

interface KeycloakContextProps {
	keycloak: Keycloak | null;
	authenticated: boolean;
	roles: Set<ROLE>;
	initialized: boolean;
	errors: string[];
}

const KeycloakContext = createContext<KeycloakContextProps | undefined>(undefined);

interface KeycloakProviderProps {
	children: React.ReactNode;
}

const keycloackConfig: KeycloakConfig = {
	url: import.meta.env.VITE_KEYCLOAK_URL,
	realm: import.meta.env.VITE_KEYCLOAK_REALM,
	clientId: import.meta.env.VITE_KEYCLOAK_CLIENT,
};
const keycloakInstance: Keycloak = new Keycloak(keycloackConfig);

const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
	const isRun = useRef<boolean>(false);
	const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
	const [authenticated, setAuthenticated] = useState<boolean>(false);
	const [roles, setRoles] = useState<Set<ROLE>>(new Set());
	const [initialized, setInitialized] = useState<boolean>(false);
	const [errors, setErrors] = useState<string[]>([]);

	const resetSession = useCallback(() => {
		setAuthenticated(false);
		setRoles(new Set());
		setAccessToken('');
		Sentry.setUser(null);
	}, []);

	const updateToken = useCallback(async () => {
		if (!keycloak) return;
		try {
			const currentToken = keycloak.token || '';
			const refreshedToken = await ensureFreshToken(TOKEN_REFRESH_MARGIN_SECONDS);
			if (refreshedToken && refreshedToken !== currentToken) {
				console.log('Token refreshed:', new Date().toLocaleTimeString());
				setAccessToken(refreshedToken);
			} else {
				console.log('Token is still valid, no refresh needed');
			}
		} catch (error) {
			if (isSessionEndedError(error)) {
				// The session is over. keycloak-js has already dropped the token and, with
				// login-required, started the redirect to the login page (which returns to
				// the current URL). Only the local state is left to clean up.
				console.warn('Session ended, login required:', error);
				resetSession();
				clearKeycloakSession();
				return;
			}
			// Transient (network, 5xx): the current token stays usable and the next
			// tick tries again. Logging out here would end a session that is still valid.
			console.warn('Token refresh failed, keeping the session:', error);
		}
	}, [keycloak, resetSession]);

	// Initialize Keycloak on component mount
	useEffect(() => {
		if (isRun.current) return;

		isRun.current = true;

		const initKeycloak = async () => {
			try {
				const authenticated = await keycloakInstance.init({
					onLoad: 'login-required',
					scope: 'openid profile email organization',
					// Login page in the app's language; Keycloak reads it as ui_locales.
					locale: getInitialLanguage(),
					// Disable the 3rd-party "login status" iframe. Browsers block 3rd-party
					// cookies, so the iframe from the Keycloak server can't message back when
					// the app runs on a different origin (e.g. localhost), causing
					// "Timeout when waiting for 3rd party check iframe message." Token refresh
					// is already handled via onTokenExpired / updateToken below.
					checkLoginIframe: false,
				});
				registerKeycloakSession(keycloakInstance);
				const roles = keycloakInstance.resourceAccess?.[keycloakInstance.clientId || '']?.roles || [];
				if (authenticated && roles.length > 0) {
					setRoles(new Set(roles as ROLE[]));
				}
				if (authenticated && keycloakInstance.token) {
					setAccessToken(keycloakInstance.token);
					Sentry.setUser({
						id: keycloakInstance.subject,
						username: keycloakInstance.tokenParsed?.preferred_username as string | undefined,
					});
				}
				setAuthenticated(authenticated);
				setErrors([]);
			} catch (error) {
				console.error('Keycloak initialization failed:', error);
				resetSession();
				clearKeycloakSession();
				localStorage.removeItem('access_token');
				if (error instanceof Error) {
					setErrors((prev) => [...prev, error.message]);
				} else {
					setErrors((prev) => [...prev, t('auth.errors.initFailed')]);
				}
			} finally {
				setKeycloak(keycloakInstance);
				setInitialized(true);
			}
		};

		initKeycloak();
	}, [resetSession]);

	// Set up token refresh before it expires
	useEffect(() => {
		if (!keycloak) return;

		const onTokenExpired = async () => {
			try {
				await updateToken();
			} catch (error) {
				console.error('Failed to refresh token:', error);
			}
		};

		keycloak.onTokenExpired = onTokenExpired;

		return () => {
			if (keycloak) {
				keycloak.onTokenExpired = undefined;
			}
		};
	}, [keycloak, updateToken]);

	useEffect(() => {
		if (!keycloak) return;

		const refreshOnWakeUp = () => {
			if (document.visibilityState === 'hidden') {
				return;
			}
			void updateToken();
		};

		window.addEventListener('focus', refreshOnWakeUp);
		window.addEventListener('pageshow', refreshOnWakeUp);
		document.addEventListener('visibilitychange', refreshOnWakeUp);

		return () => {
			window.removeEventListener('focus', refreshOnWakeUp);
			window.removeEventListener('pageshow', refreshOnWakeUp);
			document.removeEventListener('visibilitychange', refreshOnWakeUp);
		};
	}, [keycloak, updateToken]);

	// set access token to local storage for API calls
	useEffect(() => {
		if (keycloak?.token && authenticated) {
			setAccessToken(keycloak.token || '');
		}
	}, [keycloak?.token, authenticated]);

	// Periodically check if token needs refresh
	useEffect(() => {
		if (!keycloak) return;

		const refreshInterval = setInterval(() => {
			void updateToken();
		}, 10000); // Check every 10 seconds

		return () => clearInterval(refreshInterval);
	}, [keycloak, updateToken]);

	return (
		<KeycloakContext.Provider value={{ keycloak, authenticated, roles, initialized, errors }}>
			{children}
		</KeycloakContext.Provider>
	);
};

const useKeycloak = () => {
	const context = useContext(KeycloakContext);
	if (!context) {
		throw new Error('useKeycloak must be used within a KeycloakProvider');
	}

	return context;
};

export { KeycloakProvider, KeycloakContext, useKeycloak };
